import { useCallback } from 'react'
import { useCreateReport, useUpdateReport } from '~/api/supabase-mutations'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCurrentUser } from '~/common/use-current-user'
import { reportAdded, reportUpdated, useCropsContextDispatch } from '~/common/context/crops/crops-context'

export const useSubmitReport = ({
  reportCode,
  reportDate,
  notes,
  farmId,
  existingReportId,
}) => {
  const { currentUser } = useCurrentUser()
  const { createReport, loading: createLoading, error: createError } = useCreateReport()
  const { updateReport, loading: updateLoading, error: updateError } = useUpdateReport()
  const cropsDispatch = useCropsContextDispatch()

  const loading = createLoading || updateLoading
  const error = createError || updateError

  const submitReport = useCallback(async ({ onSuccess, onError } = {}) => {
    if (!reportCode || !farmId || !reportDate || !currentUser) return

    try {
      if (existingReportId) {
        // Edit mode: update occurrence date and notes only
        const { data: report, error: reportError } = await updateReport(existingReportId, {
          created_at: reportDate.toISOString(),
          notes: notes || null,
        })
        if (reportError) {
          console.error('Failed updating report record', reportError)
          onError?.(reportError)
          return
        }
        supabaseGetter.reports.invalidate()
        cropsDispatch(reportUpdated({ reportId: report.id }))
        await onSuccess?.(report)
      } else {
        // Create mode
        const { data: report, error: reportError } = await createReport({
          report_code: reportCode,
          organization_id: farmId,
          reported_by: currentUser.id,
          notes: notes || null,
          created_at: reportDate.toISOString(),
        })
        if (reportError) {
          console.error('Failed creating report record', reportError)
          onError?.(reportError)
          return
        }
        supabaseGetter.reports.invalidate()
        cropsDispatch(reportAdded({ reportId: report.id }))
        await onSuccess?.(report)
      }
    } catch (err) {
      console.error('Failed submitting report', err)
      onError?.(err)
    }
  }, [reportCode, farmId, reportDate, notes, existingReportId, currentUser, createReport, updateReport, cropsDispatch])

  return { submitReport, loading, error }
}
